import { keyframes, styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { DeployStepStatus } from '@/types/board';

// Keyframe animations
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const scaleIn = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

// Overlay container
export const OverlayContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFlush',
})<{ isFlush?: boolean }>(({ isFlush }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.80)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: isFlush ? 0 : 24,
  borderRadius: 0,
}));

// Content wrapper
export const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFlush',
})<{ isFlush?: boolean }>(({ isFlush }) => ({
  display: 'inline-flex',
  padding: isFlush ? 0 : 24,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  maxWidth: 668,
  borderRadius: 0,
}));

// Stepper container
export const StepperContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

// Step row
export const StepRow = styled(Box)({
  display: 'flex',
  gap: 16,
  alignItems: 'stretch',
  minHeight: 116,
});

// Step node (left column with icon and connector)
export const StepNode = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  width: 60,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  paddingTop: 48,
  transition: 'padding-top 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  ...(isActive && {
    paddingTop: 48,
  }),
}));

// Connector line
export const ConnectorLine = styled(Box)({
  position: 'absolute',
  left: '50%',
  top: 0,
  bottom: 0,
  width: 1,
  backgroundColor: 'var(--dk-accent-tint, var(--accent-blue-blue-24, #C2DAF3))',
  transform: 'translateX(-0.5px)',
});

// Step circle base
export const StepCircle = styled(Box)({
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
  zIndex: 1,
});

// Pending circle: 20x20, white fill, blue border
export const PendingCircle = styled(StepCircle)({
  width: 20,
  height: 20,
  backgroundColor: 'var(--dk-bg, #FFFFFF)',
  outline: '1px solid var(--dk-accent-line, var(--accent-blue-blue-24, #C2DAF3))',
  outlineOffset: -1,
});

// In Progress circle: 48x48, white fill, blue border
export const InProgressCircle = styled(StepCircle)({
  width: 48,
  height: 48,
  backgroundColor: 'var(--dk-bg, #FFFFFF)',
  outline: '1px solid var(--dk-accent-line, var(--accent-blue-blue-24, #C2DAF3))',
  outlineOffset: -1,
});

// Completed circle: 20x20, filled blue
export const CompletedCircle = styled(StepCircle)({
  width: 20,
  height: 20,
  backgroundColor: 'var(--Portal-Color-Point-color, var(--accent-blue-blue-100, #06C))',
});

// Error circle: 20x20, filled #F55060
export const ErrorCircle = styled(StepCircle)({
  width: 20,
  height: 20,
  backgroundColor: '#F55060',
});

// Step content (right column)
export const StepContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isDone',
})<{ isActive?: boolean; isDone?: boolean }>(({ isActive, isDone }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  position: 'relative',
  paddingTop: 48,
  paddingBottom: 48, // pending 상태 기본 패딩
  transition: 'padding 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  ...(isActive && {
    paddingTop: 48,
    paddingBottom: 48,
  }),
  ...(isDone && {
    paddingTop: 48,
    paddingBottom: 24,
  }),
}));

// Step label text
export const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: DeployStepStatus }>(({ status }) => ({
  fontFamily: 'var(--font-SarasaMonoK), "Sarasa Mono K", monospace',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  color:
    status === 'completed'
      ? 'var(--Portal-Color-Point-color, var(--accent-blue-blue-100, #06C))'
      : status === 'error'
      ? '#F55060'
      : '#000000',
}));

// Status text (Success/Failed)
export const StatusText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isError',
})<{ isError?: boolean }>(({ isError }) => ({
  fontFamily: 'var(--font-SarasaMonoK), "Sarasa Mono K", monospace',
  fontSize: 14,
  fontWeight: isError ? 500 : 400,
  lineHeight: '20px',
  color: isError ? '#F55060' : 'var(--Portal-Color-Point-color, var(--accent-blue-blue-100, #06C))',
  marginLeft: 4,
}));

// Elapsed time text (gray during in_progress, blue on success)
export const ElapsedTime = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isCompleted' && prop !== 'isError',
})<{ isCompleted?: boolean; isError?: boolean }>(
  ({ isCompleted, isError }) => ({
    fontFamily: 'var(--font-SarasaMonoK), "Sarasa Mono K", monospace',
    fontSize: 14,
    fontWeight: isCompleted || isError ? 500 : 400,
    lineHeight: '20px',
    color: isError ? '#F55060' : isCompleted ? 'var(--Portal-Color-Point-color, var(--accent-blue-blue-100, #06C))' : '#9CA3AF',
    marginLeft: 4,
  }),
);

// Step message
export const StepMessage = styled(Typography)({
  fontFamily:
    'var(--font-Pretendard), var(--font-noto-sans), "PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", sans-serif',
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  color: 'var(--dk-text, #000000)',
  marginTop: 4,
});

// Animated step message wrapper
export const AnimatedMessage = styled(Box)({
  animation: `${fadeInUp} 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
});

// Icon SVG wrapper with scale animation
export const AnimatedIconSvg = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
});
